import React, { useMemo } from "react";
import { Dropdown, MenuProps } from "antd";
import { useTrapsiteEditorContext } from "./TrapsiteEditorContext";
import { POPUP_Z_INDEX } from "./constants";

interface Props {
  /** Resolves the right-clicked site so the panel can be prefilled. */
  onEditTrapsite: (id: string) => void;
  /** Resolves the right-clicked trap so the panel can be prefilled. */
  onEditTrap: (uuid: string) => void;
  /** Whether this map offers creating a collection location. */
  allowCreateTrapsite: boolean;
  /** The location this map is scoped to, pre-assigned when placing a trap. */
  scopedTrapSiteId: string | null;
  /** Portal host, so the menu is not clipped and survives fullscreen. */
  container: HTMLElement | null;
}

/**
 * The right-click menu, positioned by an empty anchor at the click point.
 *
 * antd `Dropdown` rather than a hand-rolled absolute div so it inherits the
 * theme, hover and focus behaviour - matching UsersTable, which already
 * establishes Dropdown as this app's context menu.
 */
const MapContextMenu: React.FC<Props> = ({
  onEditTrapsite,
  onEditTrap,
  allowCreateTrapsite,
  scopedTrapSiteId,
  container,
}) => {
  const editor = useTrapsiteEditorContext();
  const { menu, draft, deviceDraft } = editor;

  const items: MenuProps["items"] = useMemo(() => {
    if (!menu) return [];

    // An editor is already open; pins are repositioned by dragging them.
    if (draft || deviceDraft) return [];

    if (menu.target.kind === "trapsite") {
      return [{ key: "edit", label: "Edit Collection Location" }];
    }
    if (menu.target.kind === "device") {
      return [{ key: "edit-trap", label: "Edit Trap" }];
    }

    // Empty map. Which flows are offered depends on where the map is: the
    // location detail view is already scoped to one location, so creating
    // another from it would be odd.
    return [
      ...(allowCreateTrapsite
        ? [{ key: "create", label: "Create Collection Location" }]
        : []),
      { key: "move-trap", label: "Move Trap" },
    ];
  }, [menu, draft, deviceDraft, allowCreateTrapsite]);

  if (!menu || items.length === 0) return null;

  // The menu is positioned inside `container`, so the viewport coordinates the
  // right-click gave us have to be rebased onto it. Reading the rect at render
  // rather than at click time keeps it correct if the map has since resized -
  // entering pseudo-fullscreen does exactly that.
  const rect = container?.getBoundingClientRect();
  const left = rect ? menu.x - rect.left : menu.x;
  const top = rect ? menu.y - rect.top : menu.y;

  const onClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "create" && menu.target.kind === "map") {
      editor.startCreate(menu.target.gps);
    } else if (key === "move-trap" && menu.target.kind === "map") {
      editor.startMoveTrap(menu.target.gps, scopedTrapSiteId);
    } else if (key === "edit" && menu.target.kind === "trapsite") {
      onEditTrapsite(menu.target.id);
    } else if (key === "edit-trap" && menu.target.kind === "device") {
      onEditTrap(menu.target.uuid);
    }
  };

  return (
    <div
      className="trapsite-context-menu-anchor"
      style={{ position: "absolute", left, top }}
    >
      <Dropdown
        open
        trigger={[]}
        menu={{ items, onClick }}
        getPopupContainer={() => container || document.body}
        overlayStyle={{ zIndex: POPUP_Z_INDEX }}
        onOpenChange={(open) => {
          if (!open) editor.closeMenu();
        }}
      >
        <span />
      </Dropdown>
    </div>
  );
};

export default MapContextMenu;
